import type { DupeSearchType } from './googleAdsConversions'
import { todayStamp } from './dailyConversionId'
import { zarazTrack } from './zaraz'

/**
 * TikTok's `Performed Dupe` conversion, capped at ONE PER USER PER DAY
 * (DUP-2261) — the TikTok twin of `metaConversions.ts`, fired from the same
 * call sites with the same daily id (`dailyConversionId.ts`).
 *
 * Why TikTok has its own Zaraz event now. From DUP-2125 until DUP-2261, TikTok
 * rode the per-search `dupe_performed_manual2` event alongside Google Ads, and
 * docs/references/tiktok-conversion-tracking.md recorded "no separate
 * tiktokConversions.ts" as a deliberate non-goal: one canonical event, mapped
 * per platform at the edge. That held while every platform wanted the same
 * cadence. Marketing then asked TikTok to match Meta — one conversion per
 * person per day, because these are ad clicks and a repeat search says nothing
 * new — while Google stays per-search. Two cadences cannot share one event, so
 * TikTok gets `dupe_performed_daily`. The FIRING DECISION (which searches
 * count) is still made once, at the two call sites; only the cap and the
 * dispatch live here.
 *
 * ⚠️ THE EVENT NAME BELOW IS CROSS-REPO CONTRACT. The TikTok managed component
 * on the dupe.com Cloudflare zone triggers on `dupe_performed_daily` BY NAME
 * and forwards it to TikTok's Events API as `Search`. Renaming it, or dropping
 * `event_id` from the payload, breaks TikTok SILENTLY: the trigger stops
 * matching at the edge, no error is raised anywhere in this repo, and no test
 * outside this module fails. `infra/zaraz/assert-tiktok-tool.py` asserts the
 * Cloudflare side; the spec here asserts this side.
 *
 * ⚠️ Rollout order matters, once: this module has to be DEPLOYED before the
 * Cloudflare trigger is repointed from `dupe_performed_manual2` to
 * `dupe_performed_daily`. Flipped first, TikTok receives nothing until the
 * deploy lands. Flipped second, TikTok stays per-search until the flip, which
 * is only the status quo.
 *
 * ⚠️ Two layers enforce the cap, and they cover DIFFERENT failures:
 *   1. our own guard below, which stops the redundant `zaraz.track()` call,
 *      and
 *   2. TikTok's own dedup on `event_id` + `pixel_code`, which covers the SAME
 *      browser losing its mark (cleared storage, private mode, a quota or
 *      policy failure in the try/catch below). TikTok documents that dedup
 *      for pixel-plus-API pairs; whether it also collapses two API-only sends
 *      is NOT verified, which is exactly why layer 1 exists rather than
 *      leaning on layer 2. Neither layer covers a second device; nothing here
 *      can (see `dailyConversionId.ts`).
 *
 * ⚠️ Like every Zaraz conversion, this one is gated upstream by
 * `NEXT_PUBLIC_ZARAZ_TRACK_ENABLED` — see `googleAdsConversions.ts` for why
 * that is the biggest silent failure in the chain.
 */

const EVENT_NAME = 'dupe_performed_daily'

const firedConversions = new Set<string>()
const STORAGE_PREFIX = 'tiktok-conversion:'
const LOCK_PREFIX = 'tiktok-conversion-lock:'

/**
 * `localStorage`, not `sessionStorage`: the cap is per DAY, so the mark has to
 * outlive a tab. `googleAdsConversions.ts` uses `sessionStorage` because its
 * key is per search and a tab is long enough. Keys are self-expiring by
 * construction — they carry the date, so yesterday's key is simply never read
 * again — and `pruneStaleMarks` sweeps them so the store cannot grow without
 * bound.
 */
const hasFired = (key: string): boolean => {
  if (firedConversions.has(key)) return true

  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${key}`) !== null
  } catch {
    // localStorage can be unavailable in privacy-restricted browsers. The
    // module-level set still prevents duplicate React effects in that case.
    return false
  }
}

const DATED_KEY = /:\d{4}-\d{2}-\d{2}$/
const UNDATED_MARK_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Drop marks that can no longer be read. Same two classes, same rules as
 * `metaConversions.ts`, and deliberately scoped to THIS module's prefix — the
 * Meta sweep owns the Meta prefixes; two sweeps touching each other's keys is
 * how one of them ends up deleting a mark the other still needs.
 *
 *   dated (`…:<user>:<YYYY-MM-DD>`) — anything not from today is dead, since
 *   the id it would match can never be generated again.
 *
 *   undated (chat's no-`tempUserId` fallback, a per-search key) — NOT safe to
 *   drop on sight: removing one mid-session lets that same search re-fire.
 *   They age out on their stored timestamp instead.
 */
const pruneStaleMarks = (): void => {
  try {
    const today = todayStamp()
    const cutoff = Date.now() - UNDATED_MARK_TTL_MS
    const stale: string[] = []

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i)
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue

      if (DATED_KEY.test(k)) {
        if (!k.endsWith(`:${today}`)) stale.push(k)
        continue
      }

      const writtenAt = Number.parseInt(
        window.localStorage.getItem(k) ?? '',
        10
      )
      if (!Number.isFinite(writtenAt) || writtenAt < cutoff) stale.push(k)
    }

    for (const k of stale) window.localStorage.removeItem(k)
  } catch {
    // See hasFired.
  }
}

/**
 * Only ever called once `zarazTrack` reports it reached `zaraz.track()`. The
 * mark is durable for the rest of the DAY, so writing it on a no-op (rollout
 * flag off, or the edge script not attached yet) would suppress the
 * conversion until tomorrow with no way to retry.
 */
const markFired = (key: string): void => {
  firedConversions.add(key)
  pruneStaleMarks()

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, `${Date.now()}`)
  } catch {
    // See hasFired: privacy-restricted browsers throw here.
  }
}

/**
 * Guard, dispatch, and mark — in that order, in one place. Web Locks serialize
 * that sequence across tabs: `localStorage` operations are individually atomic,
 * but an unlocked get→send→set sequence is not, so two tabs completing at once
 * could both send before either wrote the durable mark.
 *
 * The check is repeated INSIDE the lock, after waiting, so the second tab sees
 * the first tab's mark. Unsupported browsers and a page being hidden keep the
 * synchronous fallback: the results hook flushes from `pagehide`, where an
 * asynchronous lock callback might never run. Losing conversions would be
 * worse than the small duplicate risk in those fallback cases.
 */
const fireOnce = (
  event: string,
  conversionId: string,
  extraParams?: Record<string, unknown>
): void => {
  const key = `${event}:${conversionId}`
  const dispatchIfNeeded = (): void => {
    if (hasFired(key)) return

    // `transaction_id` and `event_id` last: they are the key this function just
    // deduped on, so a caller's extras must never be able to overwrite them and
    // leave the stored mark disagreeing with what went on the wire. `event_id`
    // is what the Cloudflare action maps to TikTok's dedupe key (field
    // "Event ID" → "Event Property: event_id"); `transaction_id` keeps the
    // payload shape identical to `dupe_performed_manual2` so anything reading
    // both sees one schema.
    const dispatched = zarazTrack(event, {
      ...extraParams,
      transaction_id: conversionId,
      event_id: conversionId,
    })
    if (dispatched) markFired(key)
  }

  if (hasFired(key)) return

  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  const pageIsBeingHidden =
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  if (!locks || pageIsBeingHidden) {
    dispatchIfNeeded()
    return
  }

  void locks
    .request(`${LOCK_PREFIX}${key}`, dispatchIfNeeded)
    // A browser-level lock failure should not silently drop the day's only
    // conversion. The repeated check keeps this safe if the callback ran first.
    .catch(dispatchIfNeeded)
}

/**
 * TikTok `Search` for an executed search, capped at one per user per day.
 *
 * `conversionId` should come from `dailyConversionId`. Callers that cannot
 * identify the user fall back to a per-search id — an uncapped conversion is
 * the conservative failure, since the alternative is losing it entirely. The
 * results-page hook never falls back (it gates on `tempUserId`); chat does.
 */
export const trackTikTokDupePerformed = ({
  conversionId,
  searchType,
}: {
  conversionId: string
  searchType: DupeSearchType
}): void => {
  fireOnce(EVENT_NAME, conversionId, { search_type: searchType })
}
